import { KeyboardEvent, MouseEvent, useState } from 'react'

import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import { Box, Stack } from '@mui/material'
import { CircleFlag } from '@/components/CircleFlag'
import { ConsoleTooltip } from '@/components/ConsoleTooltip'
import { CountryTerritory } from '@/constants/countriesAndTerritories'

import { usePhoneNumber } from '../../../hooks/usePhoneNumber'
import { CountryNumberPrefixSelectPopper } from '../CountryNumberPrefixSelectPopper'
import { ConsoleTextField } from '../StyledInputComponents'

type Props = {
  defaultValue?: string
  onChange?: (value: string) => void
  label?: string
  placeholder?: string
  disabled?: boolean
  required?: boolean
  error?: string
}

export const PhoneNumberInput = (props: Props) => {
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null)
  const {
    formattedNumber,
    selectedCountry,
    placeholder,
    inputRef,
    handleInput,
    handleInputFocus,
    setCountry
  } = usePhoneNumber({
    defaultValue: props.defaultValue,
    onChange: props.onChange,
    placeholder: props.placeholder
  })
  const handleClickMenuTrigger = (event: MouseEvent<HTMLElement>) => {
    if (menuAnchorEl) {
      setMenuAnchorEl(null)
      return
    }

    setMenuAnchorEl(event.currentTarget)
  }

  const handleKeyDownMenuTrigger = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key === 'Enter' || event.key === ' ' || event.key === 'ArrowDown') {
      event.preventDefault()
      if (!menuAnchorEl) {
        setMenuAnchorEl(event.currentTarget)
      }
    } else if (event.key === 'Escape') {
      setMenuAnchorEl(null)
    }
  }

  const handleCloseMenu = () => {
    setMenuAnchorEl(null)
  }
  const handleCountrySelect = (country: CountryTerritory) => {
    setCountry(country)
    handleCloseMenu()
  }

  const handleInputKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'ArrowDown' && !menuAnchorEl) {
      event.preventDefault()
      const triggerButton = event.currentTarget.querySelector('[role="button"]') as HTMLElement
      if (triggerButton) {
        setMenuAnchorEl(triggerButton)
      }
    }
  }

  return (
    <>
      <ConsoleTextField
        inputRef={inputRef}
        label={props.label || 'Phone Number'}
        value={formattedNumber}
        onChange={handleInput}
        onFocus={handleInputFocus}
        onKeyDown={handleInputKeyDown}
        placeholder={placeholder}
        disabled={props.disabled}
        required={props.required}
        error={props.error}
        type="tel"
        slotProps={{
          input: {
            endAdornment: (
              <Stack
                direction="row"
                alignItems="center"
                spacing={0.25}
                tabIndex={0}
                role="button"
                onClick={handleClickMenuTrigger}
                onKeyDown={handleKeyDownMenuTrigger}
                aria-label="Select country"
                aria-expanded={Boolean(menuAnchorEl)}
                aria-haspopup="listbox"
                sx={{
                  backgroundColor: ({ palette }) => palette.phoneInput.buttonBackground,
                  height: '100%',
                  mr: -1.75,
                  pl: 1,
                  pr: 0.5,
                  borderLeft: '1px solid',
                  borderLeftColor: ({ palette }) => palette.divider,
                  borderTopRightRadius: 4,
                  borderBottomRightRadius: 4,
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: ({ palette }) => palette.phoneInput.hoverButtonBackground
                  },
                  '&:focus': {
                    outline: '2px solid',
                    outlineOffset: '-2px',
                    outlineColor: 'primary.main'
                  }
                }}
              >
                <Box
                  sx={{
                    width: 22,
                    height: 22,
                    borderRadius: '50%',
                    border: '1px solid',
                    borderColor: ({ palette }) => palette.phoneInput.flagBorder,
                    overflow: 'hidden'
                  }}
                >
                  <ConsoleTooltip
                    title={selectedCountry?.name || 'Unknown Country'}
                    distance={7}
                    enterDelay={500}
                  >
                    <CircleFlag
                      countryCode={selectedCountry?.iso2 || 'gb'}
                      height="100%"
                      width="100%"
                    />
                  </ConsoleTooltip>
                </Box>
                <ArrowDropDownIcon
                  color="action"
                  sx={{
                    transform: menuAnchorEl ? 'rotate(180deg)' : 'rotate(0deg)'
                  }}
                />
              </Stack>
            )
          }
        }}
      />
      <CountryNumberPrefixSelectPopper
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleCloseMenu}
        onCountrySelect={handleCountrySelect}
      />
    </>
  )
}
